import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { Helmet } from "react-helmet";
import { Container, Row, Col, Card } from 'react-bootstrap';
import isEmpty from "lodash.isempty";

// components
import Footer from "../../components/Footer";
import MenuLocaleSelect from "../../components/MenuLocaleSelect";
import Logo from "../../components/Logo";
import SignInEmail from "../../components/SignInEmail";
import SignInCode from "../../components/SignInCode";

// hooks & services
import useUserAccount from "../../hooks/useUserAccount";
import useEnv from "../../hooks/useEnv";
import useI18n from "../../hooks/useI18n";
import usePasskeys from "../../hooks/usePasskeys";
import { toggleLoading } from "../../services/helpers";
import { CUSTOMERTYPE } from "../../contexts/AuthContext";

const messages = defineMessages({
    returnToFrontPage: {
        id: "Palaa takaisin etusivulle",
        defaultMessage: "Palaa takaisin etusivulle"
    },
    login: {
        id: "Kirjaudu MyRopo-palveluun",
        defaultMessage: "Kirjaudu MyRopo-palveluun"
    },
    loading: {
        id: "Ladataan",
        defaultMessage: "Ladataan"
    },
    passkeyError: {
        id: "Passkey-kirjautuminen ei ole käytettävissä tällä selaimella. Paina 'Jatka', niin saat kertakäyttöisen koodin sähköpostiisi.",
        defaultMessage: "Passkey-kirjautuminen ei ole käytettävissä tällä selaimella. Paina 'Jatka', niin saat kertakäyttöisen koodin sähköpostiisi."
    },
    passkeyCanceled: {
        id: "Passkey-kirjautuminen peruutettu. Paina 'Jatka', niin saat kertakäyttöisen koodin sähköpostiisi.",
        defaultMessage: "Passkey-kirjautuminen peruutettu. Paina 'Jatka', niin saat kertakäyttöisen koodin sähköpostiisi."
    },
    somethingWentWrong: {
        id: "Tapahtui virhe. Ole hyvä ja yritä uudelleen.",
        defaultMessage: "Tapahtui virhe. Ole hyvä ja yritä uudelleen."
    },
    codeResentSuccess: {
        id: "Uusi koodi lähetetty sähköpostiisi.",
        defaultMessage: "Uusi koodi lähetetty sähköpostiisi."
    }
});

export { messages };

const SignIn = (props) => {
    const lng = props.intl.formatMessage;
    const navigate = useNavigate();
    const location = useLocation();
    const confirmAction = props.confirmAction ?? "";
    const SIGN_IN_CODE_LENGTH = 8;
    const SIGN_UP_CODE_LENGTH = 6;
    const confirmationCodeMaxLength = confirmAction === "confirmSignIn" ? SIGN_IN_CODE_LENGTH : SIGN_UP_CODE_LENGTH; // 8 for sign in, 6 for sign up
    const {
        userSignInPasswordless, userAuthenticate, userSignUp, userIsLoggedIn,
        userAutoSignIn, getAuthTokens, userConfirmSignIn, userConfirmSignUp, userResendSignUpCode,
        userSetChosenCustomerType, deleteRefreshToken, userIdentityVerificationUrl, userSignOut
    } = useUserAccount();
    const { registerPasskey, getPasskeys, passkeysEnabled, getRelatedOrigins } = usePasskeys();
    const { isDev } = useEnv();
    const { getLocale } = useI18n();
    const [error, setError] = useState("");
    const [emailIsSubmitting, setEmailIsSubmitting] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [alert, setAlert] = useState(null);
    const [preferredChallenge, setPreferredChallenge] = useState(passkeysEnabled ? 'WEB_AUTHN' : 'EMAIL_OTP'); // default to passkey
    // Cognito errors
    const UserNotFoundException = 'User does not exist.';
    const CodeMismatchException = 'Invalid code or auth state for the user.';
    const AutoSignInException = 'The autoSignIn flow has not started';
    const RelyingPartyException = 'Relying party does not match current domain.';
    const PasskeyCanceledException = 'Passkey authentication ceremony has been canceled.';
    const UserAlreadyAuthenticatedException = 'There is already a signed in user.';
    const customerType = new URLSearchParams(location?.search).get('customerType');

    useEffect(() => {
        if (!isEmpty(customerType)) {
            console.log("Setting customer type from URL params:", customerType);
            userSetChosenCustomerType(customerType);
        }
    }, [customerType]);

    useEffect(() => {
        if (userIsLoggedIn()) {
            // if user is already logged in, move directly to dashboard
            navigate("/jobsearch", { replace: true });
        } else if (isEmpty(location?.state?.email) && window.location.href.includes('confirm')) {
            // Handle the case where we are at confirm phase and user reloads the page
            navigate("/user/signin", { replace: true });
        }
    }, []);

    /**
     * Handle the next step after a sign in attempt.
     * @param {string} nextStep
     * @param {string} email
     * @returns {Promise<string | null>}
     */
    const handleAuthNextStep = async (nextStep, email) => {
        let redirectUrl = null;
        // If email is not confirmed, we need to resend the sign-up code (the process was not completed earlier)
        switch (nextStep) {
            case 'CONFIRM_SIGN_UP':
                if (isDev) {
                    console.log("User is not confirmed, sending sign-up code again");
                }
                await userResendSignUpCode(email);
                redirectUrl = "/user/confirmsignup";
                break;
            case 'DONE':
                if (isDev) {
                    console.log("Sign in completed, proceed to dashboard");
                }
                await handleSignIn();
                break;
            case 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION':
                if (isDev) {
                    console.log("Sign in method not available, defaulting to EMAIL_OTP");
                }
                await userConfirmSignIn('EMAIL_OTP');
                redirectUrl = "/user/confirmsignin";
                break;
            default:
                if (isDev) {
                    console.log("Proceeding to confirm sign in for step: ", nextStep);
                }
                redirectUrl = "/user/confirmsignin";
        }
        return redirectUrl;
    }

    /**
     * Submit email and try to sign in.
     * 
     * First try to sign in using OTP. If error is returned,
     * a user with that email is not found, so a new user must be created.
     * 
     * @param {string} email
     * @param {boolean} rememberMe
     */
    const handleSubmitEmail = async (email, rememberMe) => {
        setEmailIsSubmitting(true);
        setAlert(null);
        try {
            if (rememberMe === true && !isEmpty(email)) {
                localStorage.setItem('defaultEmail', email);
            } else if (rememberMe === false) {
                localStorage.setItem('defaultEmail', '-');
            }
            // try to sign in with one time password to see if the user exists
            const nextStep = await userSignInPasswordless(email, preferredChallenge);

            if (isDev) {
                console.log("userSignInOTP success");
            }

            const redirectUrl = await handleAuthNextStep(nextStep, email);

            if (redirectUrl !== null) {
                navigate(redirectUrl, { state: { email, rememberMe } });
            }

            setAlert(null);
        } catch (error) {
            // print to console in dev environment for diagnostics
            if (isDev) {
                console.error("userSignInOTP failed:", error);
            }
            switch (error.message) {
                case UserNotFoundException:
                    await handleSignUp(email, rememberMe);
                    break;
                case PasskeyCanceledException:
                    setAlert({
                        message: lng(messages.passkeyCanceled),
                        type: 'primary'
                    });
                    setPreferredChallenge('EMAIL_OTP');
                    break;
                case RelyingPartyException:
                    setAlert({
                        message: lng(messages.passkeyError),
                        type: 'warning'
                    });
                    setPreferredChallenge('EMAIL_OTP');
                    localStorage.setItem('disablePasskeys', 'true');
                    break;
                case UserAlreadyAuthenticatedException:
                    // User is already authenticated, sign them out first
                    await userSignOut();
                    await handleSubmitEmail(email, rememberMe);
                    return;
                default:
                    setAlert({
                        message: lng(messages.somethingWentWrong),
                        type: 'danger'
                    });
                    setPreferredChallenge('EMAIL_OTP');
            }
        } finally {
            setEmailIsSubmitting(false);
        }
    }

    /**
     * Sign up a new user.
     * 
     * @param {string} email
     * @param {boolean} rememberMe
     */
    const handleSignUp = async (email, rememberMe) => {
        try {
            // try to sign up a new user
            await userSignUp({
                email,
                locale: getLocale()
            });

            // on success move to confirmsignup for confirmation code input
            navigate("/user/confirmsignup", { state: { email, rememberMe } });

            setAlert(null);
        } catch (error) {
            // print to console in dev environment for diagnostics
            if (isDev) {
                console.error("userSignUp failed:", error);
            }

            // show generic error message
            setAlert({
                message: lng(messages.somethingWentWrong),
                type: 'danger'
            });
        }
    }

    /**
     * Submit a confirmation code.
     * Check the action to either confirm the sign in or sign up process.
     * 
     * @param {string} code
     * @param {string} email
     */
    const submitConfirmationCode = async (code, email) => {
        // first clear any errors
        setError("");
        try {
            toggleLoading(true, lng(messages.loading) + "...");
            // continue process differently for sign in and sign up
            if (confirmAction === "confirmSignIn") {
                // confirm the sign-in process of an existing user
                await userConfirmSignIn(code);
                await handleSignIn();
            } else if (confirmAction === "confirmSignUp") {
                // confirm the sign-up process of a new user
                await userConfirmSignUp(email, code);
                // always do an auto sign in first after confirming the sign up
                await handleAutoSignIn();
            } else {
                // if no action is specified, throw an error
                throw new Error("Invalid action");
            }

            setAlert(null);
        } catch (error) {
            // print to console in dev environment for diagnostics
            if (isDev) {
                console.error(`${confirmAction} failed: ${error}`);
            }
            if (error.message.includes(AutoSignInException)) {
                // Auto signup failed, need to sign in manually
                navigate("/user/signin", { replace: true });
            } else if (error.message === CodeMismatchException) {
                // show error if code is incorrect
                setError('incorrectCode');
            } else {
                // show generic error message when something else goes wrong
                setAlert({
                    message: lng(messages.somethingWentWrong),
                    type: 'danger'
                });
            }
        } finally {
            toggleLoading(false);
        }
    };

    /**
     * Handle auto sign in after confirming the sign-up.
     */
    const handleAutoSignIn = async () => {
        try {
            await userAutoSignIn();
            await handleSignIn();
            setAlert(null);
        } catch (error) {
            toggleLoading(false);
            // print to console in dev environment for diagnostics
            if (isDev) {
                console.error("userAutoSignIn failed:", error);
            }
            // show generic error message
            setAlert({
                message: lng(messages.somethingWentWrong),
                type: 'danger'
            })
        }
    }

    /**
     * Handle sign in by getting the authentication token from Cognito.
     * Then continue to authentication in R1.
     * @returns {Promise<void>}
     */
    const handleSignIn = async () => {
        try {
            const tokens = await getAuthTokens();
            // on success 
            if (tokens?.accessToken) {
                await authenticateAndMoveToDashboard(tokens?.accessToken);
            }
            setAlert(null);
        } catch (error) {
            toggleLoading(false);
            // print to console in dev environment for diagnostics
            if (isDev) {
                console.error("handleSignIn - getAuthTokens failed:", error);
            }
            // show generic error message
            setAlert({
                message: lng(messages.somethingWentWrong),
                type: 'danger'
            })
        }
    };

    /**
     * Try to register a passkey for the current domain, if not already registered.
     * @returns {Promise<void>}
     */
    const tryToRegisterPasskey = async () => {
        const passKeys = await getPasskeys();
        const hostname = window.location.hostname;
        const origins = getRelatedOrigins();
        const passKey = passKeys.find(k =>
            hostname.endsWith(k.relyingPartyId) ||
            (origins.includes(window.location.origin) && origins.some(o => o.endsWith(k.relyingPartyId)))
        );
        if (passKey !== undefined) {
            if (isDev) {
                console.log(`Passkey for this domain (${hostname}) already registered:`, passKey);
            }
            return;
        }
        if (isDev) {
            console.log(`Passkey for this domain (${hostname}) not registered. Trying to register...`);
        }
        await registerPasskey();
    }

    /**
     * Authenticate the user in R1, to get a JWT token for MyRopo.
     * Finally move to the dashboard.
     * @param {string} accessToken
     */
    const authenticateAndMoveToDashboard = async (accessToken) => {
        try {
            // authenticate user with Cognito access token
            await userAuthenticate(accessToken);
            if (passkeysEnabled) {
                await tryToRegisterPasskey();
            }
            // Always delete refresh token here to limit Cognito session length
            deleteRefreshToken();
            // move to dashboard
            navigate("/jobsearch");
        } catch (error) {
            // print to console in dev environment for diagnostics
            if (isDev) {
                console.error("getAuthTokens failed:", error);
            }
            navigate({ pathname: '/logout', search: '?alert=authError' });
        } finally {
            toggleLoading(false);
        }
    }

    const handleResendCode = async () => {
        setSendingCode(true);
        setAlert(null);
        const email = location?.state?.email || '';
        try {
            if (confirmAction === "confirmSignIn") {
                await userSignInPasswordless(email, 'EMAIL_OTP');
            } else if (confirmAction === "confirmSignUp") {
                await userResendSignUpCode(email);
            }
            setAlert({
                message: lng(messages.codeResentSuccess),
                type: 'success'
            });
        } catch (error) {
            if (isDev) {
                console.error("Resending code failed:", error);
            }
            setAlert({
                message: lng(messages.somethingWentWrong),
                type: 'danger'
            })
        } finally {
            setSendingCode(false);
        }
    }

    if (userIsLoggedIn()) {
        return <></>
    }

    return (
        <Container id="sign-in-page">
            <Helmet>
                <title>{lng(messages.login)} | MyRopo</title>
            </Helmet>
            <Row className="mt-4 mb-3 align-items-center">
                <Col>
                    <Link to="/logout" >
                        <Logo ariaLabel={lng(messages.returnToFrontPage)} height={34} width={128} />
                    </Link>
                </Col>
                <Col>
                    <Col xs={12} className="d-flex justify-content-end">
                        <MenuLocaleSelect />
                    </Col>
                </Col>
            </Row>
            <Row className="d-flex justify-content-center">
                <Card className="authentication">
                    {isEmpty(confirmAction) ? (
                        <SignInEmail
                            handleSubmitEmail={handleSubmitEmail}
                            strongIdentificationUrl={userIdentityVerificationUrl(customerType === CUSTOMERTYPE.COMPANY)}
                            isSubmitting={emailIsSubmitting}
                            alert={alert}
                        />
                    ) : (
                        <SignInCode
                            loading={sendingCode}
                            confirmAction={confirmAction}
                            codeLength={confirmationCodeMaxLength}
                            onSubmit={submitConfirmationCode}
                            onResendCode={handleResendCode}
                            error={error}
                            email={location?.state?.email || ""}
                            alert={alert}
                        />
                    )}
                </Card>
            </Row>
            <Row className="d-flex justify-content-center">
                <Footer />
            </Row>
        </Container>
    )
};

SignIn.propTypes = {
    'intl': PropTypes.object.isRequired,
    'confirmAction': PropTypes.string,
};

export default injectIntl(SignIn);
